import PropTypes from 'prop-types';
import React, { useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { legalDisclaimer } from '../aiConfig';

import LinkButton from './LinkButton.tsx';
import TextWithFeatures from './TextWithFeatures';

//------------------------------------------------------------------------
// AssistantResponse Component
//------------------------------------------------------------------------
export default function AssistantResponse({ message, submitAutoPrompt, responseIsJson, onManageMemories }) {
  let responseObject = null;
  let response = null;

  if (responseIsJson) {
    try {
      responseObject = JSON.parse(message.message);
    } catch (e) {
      return <Alert severity='error'>Error parsing JSON response</Alert>;
    }
    response = responseObject?.response;
  } else {
    response = message.message;
  }

  return (
    <React.Fragment key={`assistant-response-${message.id || message.message.slice(-5)}`}>
      {responseObject?.memory && responseObject.memory.length > 0 && (
        <MemoryIndicator onManageMemories={onManageMemories} memory={responseObject.memory} />
      )}
      <TextWithFeatures
        text={response}
        followUpFunction={(text) => (submitAutoPrompt ? submitAutoPrompt(text) : undefined)}
      />
      {responseObject?.relatedQuestions?.length > 0 && (
        <>
          <Divider sx={{ marginY: 1 }} />
          {responseObject.relatedQuestions.map((question, index) => (
            <LinkButton
              key={`pgph-followup-${question}-${index}`}
              onClick={() => (submitAutoPrompt ? submitAutoPrompt(question) : undefined)}
            >
              {question}
            </LinkButton>
          ))}
        </>
      )}
      {responseObject?.legalDisclaimer && (
        <Alert severity='info' sx={{ marginY: 1 }}>
          <b>Legal Disclaimer: </b>
          {legalDisclaimer}
        </Alert>
      )}
    </React.Fragment>
  );
}

AssistantResponse.propTypes = {
  message: PropTypes.any.isRequired,
  submitAutoPrompt: PropTypes.func,
  responseIsJson: PropTypes.bool,
  onManageMemories: PropTypes.func,
};

//------------------------------------------------------------------------
// MemoryIndicator Component
//------------------------------------------------------------------------
function MemoryIndicator({ memory, onManageMemories }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);

  const memoryContent = Array.isArray(memory) ? memory.join(', ') : memory;

  // Handlers for keeping track of hover state on both text and popover
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setTimeout(() => setIsHovered(false), 100); // Small delay to avoid flicker
  const handleMenuMouseEnter = () => setIsMenuHovered(true);
  const handleMenuMouseLeave = () => setTimeout(() => setIsMenuHovered(false), 100); // Small delay to avoid flicker

  return (
    <Box
      sx={{ background: '#eeeeee', display: 'inline-block', marginY: 1, position: 'relative' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Memory Updated Text */}
      <Typography variant='body2' sx={{ color: 'primary.main', cursor: 'pointer' }}>
        Memory updated
      </Typography>

      {/* Popover for memory content and manage button */}
      {(isHovered || isMenuHovered) && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
            padding: 2,
            marginTop: 1,
            width: 'max-content',
            maxWidth: 300,
          }}
          onMouseEnter={handleMenuMouseEnter}
          onMouseLeave={handleMenuMouseLeave}
        >
          {/* Memory content */}
          <Typography variant='body2' sx={{ marginBottom: 1 }}>
            {memoryContent}
          </Typography>

          {/* Manage memories button */}
          <Button
            variant='text'
            size='small'
            onClick={() => (onManageMemories ? onManageMemories() : undefined)}
            sx={{ textTransform: 'none' }}
          >
            Manage memories
          </Button>
        </Paper>
      )}
    </Box>
  );
}

MemoryIndicator.propTypes = {
  memory: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  onManageMemories: PropTypes.func,
};
